import { LitElement, html } from 'lit'
import { ModalBaseMixin } from '../core/mixins/modal-base.mixin.js'
import { apiService } from '../core/services/api.service.js'
import { transacaoCacheService } from '../core/services/transacao-cache.service.js'

/**
 * Modal para gerenciamento de recorrências de transações
 * Permite visualizar, editar e excluir ocorrências individuais
 * Integra com endpoint de regeneração para propagar alterações da transação pai
 *
 * Layout: flexbox com header fixo, conteúdo com scroll independente e footer fixo
 * com botões de ação sempre visíveis (padrão 040-modal-scroll-layout-padronizado)
 */
class ModalRecorrencias extends ModalBaseMixin(LitElement) {
  static properties = {
    transaction: { type: Object },
    occurrences: { type: Array },
    /** Indica se a regeneração está em andamento (controla loading do botão) */
    regenerating: { type: Boolean },
    /** Lista de ocorrências retornadas pelo backend após regeneração */
    ocorrencias: { type: Array },
    /** Indica se a regeneração já foi realizada (para exibir mensagem de lista vazia) */
    _regeneracaoRealizada: { type: Boolean, state: true },
    /** Controla estado de loading durante operação de salvamento */
    _saving: { type: Boolean, state: true }
  }

  createRenderRoot() {
    return this
  }

  constructor() {
    super()
    this.open = false
    this.transaction = null
    this.occurrences = []
    this.regenerating = false
    this.ocorrencias = []
    this._regeneracaoRealizada = false
    this._saving = false
  }

  /**
   * Fecha o modal delegando ao mixin base.
   * O mixin gerencia open=false, disparo de evento modal-closed
   * e integração com History API.
   * Reseta o estado de loading para garantir estado limpo na próxima abertura.
   *
   * @param {object} [options={}] - Opções de fechamento repassadas ao mixin base
   * @param {boolean} [options.fromPopstate=false] - Indica se veio do popstate
   */
  close (options = {}) {
    this._saving = false
    super.close(options)
  }

  show(transaction) {
    this.transaction = transaction
    this._generateOccurrences()
    this.open = true
  }

  _generateOccurrences() {
    if (!this.transaction?.recurring) return

    const recurringConfig = this.transaction.recurringConfig || {}
    const occurrenceValue = Math.abs(this.transaction.amount)
    
    if (recurringConfig.endType === 'count') {
      this.occurrences = Array.from({ length: recurringConfig.count }, (_, index) => {
        const occurrenceNumber = index + 1
        const date = this._calculateOccurrenceDate(this.transaction.date, index)
        
        return {
          number: occurrenceNumber,
          date,
          amount: occurrenceValue,
          value: occurrenceValue,
          isCurrent: occurrenceNumber === 1
        }
      })
    } else if (recurringConfig.endType === 'date') {
      try {
        const [startDay, startMonth, startYear] = (this.transaction?.date || '01/01/2024').split('/')
        const startDate = new Date(startYear, startMonth - 1, startDay)
        const [endDay, endMonth, endYear] = (recurringConfig.endDate || '01/01/2024').split('/')
        const endDate = new Date(endYear, endMonth - 1, endDay)
        
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          this.occurrences = [{
            number: 1,
            date: this.transaction?.date || '01/01/2024',
            amount: occurrenceValue,
            value: occurrenceValue,
            isCurrent: true
          }]
        } else {
          this.occurrences = this._generateOccurrencesByEndDate(startDate, endDate)
        }
      } catch (error) {
        this.occurrences = [{
          number: 1,
          date: this.transaction?.date || '01/01/2024',
          amount: occurrenceValue,
          value: occurrenceValue,
          isCurrent: true
        }]
      }
    } else {
      this.occurrences = [{
        number: 1,
        date: this.transaction?.date || '01/01/2024',
        amount: occurrenceValue,
        value: occurrenceValue,
        isCurrent: true
      }]
    }
  }

  _calculateOccurrenceDate(baseDate, monthsToAdd) {
    try {
      const [day, month, year] = baseDate.split('/')
      const originalDay = parseInt(day)
      const originalMonth = parseInt(month) - 1
      const originalYear = parseInt(year)
      
      if (isNaN(originalDay) || isNaN(originalMonth) || isNaN(originalYear)) {
        return 'Data inválida'
      }
      
      let newMonth = originalMonth + monthsToAdd
      let newYear = originalYear
      
      while (newMonth >= 12) {
        newMonth -= 12
        newYear++
      }
      
      while (newMonth < 0) {
        newMonth += 12
        newYear--
      }
      
      const lastDayOfMonth = new Date(newYear, newMonth + 1, 0).getDate()
      const finalDay = Math.min(originalDay, lastDayOfMonth)
      
      const finalDate = new Date(newYear, newMonth, finalDay)
      return finalDate.toLocaleDateString('pt-BR')
    } catch (error) {
      return 'Data inválida'
    }
  }

  _generateOccurrencesByEndDate(startDate, endDate) {
    const occurrences = []
    let currentDate = new Date(startDate)
    const finalDate = new Date(endDate)
    let occurrenceNumber = 1
    
    const amount = this.transaction?.amount ? Math.abs(this.transaction.amount) : 100
    
    if (finalDate < currentDate) {
      return [{
        number: 1,
        date: currentDate.toLocaleDateString('pt-BR'),
        amount: amount,
        value: amount,
        isCurrent: true
      }]
    }
    
    while (currentDate <= finalDate && occurrenceNumber <= 100) {
      occurrences.push({
        number: occurrenceNumber,
        date: currentDate.toLocaleDateString('pt-BR'),
        amount: amount,
        value: amount,
        isCurrent: occurrenceNumber === 1
      })
      
      currentDate.setMonth(currentDate.getMonth() + 1)
      occurrenceNumber++
    }
    
    return occurrences
  }

  _getRecurringEndInfo() {
    const recurringConfig = this.transaction?.recurringConfig || {}
    
    if (recurringConfig.endType === 'count') {
      return html`
        <div class="column is-6">
          <p class="has-text-grey is-size-7">Total de Ocorrências</p>
          <p class="has-text-weight-medium">${this.occurrences.length}</p>
        </div>
      `
    } else if (recurringConfig.endType === 'date') {
      return html`
        <div class="column is-6">
          <p class="has-text-grey is-size-7">Data de Término</p>
          <p class="has-text-weight-medium">${recurringConfig.endDate || 'Não definida'}</p>
        </div>
      `
    } else {
      return html``
    }
  }

  _formatCurrency(amount) {
    const isNegative = amount < 0
    const absAmount = Math.abs(amount)
    const formatted = absAmount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
    
    return isNegative ? `R$ -${formatted.replace('R$ ', '')}` : formatted
  }

  _editOccurrence(occurrenceNumber) {
    const occurrence = this.occurrences.find(o => o.number === occurrenceNumber)
    if (occurrence) {
      const occurrenceTransaction = {
        ...this.transaction,
        date: occurrence.date,
        amount: this.transaction.amount >= 0 ? occurrence.value : -occurrence.value
      }
      
      this.close()
      this.dispatchEvent(new CustomEvent('edit-occurrence', {
        detail: { transaction: occurrenceTransaction, occurrenceNumber },
        bubbles: true
      }))
    }
  }

  _deleteOccurrence(occurrenceNumber) {
    if (!this.transaction) {
      return
    }
    
    if (confirm(`Tem certeza que deseja excluir a ocorrência ${occurrenceNumber}?`)) {
      this.dispatchEvent(new CustomEvent('delete-occurrence', {
        detail: { transactionId: this.transaction.id, occurrenceNumber },
        bubbles: true
      }))
    }
  }

  _deleteAllOccurrences() {
    if (!this.transaction) {
      return
    }
    
    if (confirm('Tem certeza que deseja excluir toda a série? Esta ação não pode ser desfeita.')) {
      this.dispatchEvent(new CustomEvent('delete-all-occurrences', {
        detail: { transactionId: this.transaction.id },
        bubbles: true
      }))
      this.close()
    }
  }

  /**
   * Verifica se a transação atual é uma transação pai recorrente
   * Uma transação pai recorrente possui recurring: true e NÃO possui transacaoPaiId
   * (transações filhas possuem transacaoPaiId apontando para a pai)
   * @returns {boolean} True se é transação pai recorrente
   */
  _isTransacaoPaiRecorrente() {
    return !!(this.transaction?.recurring && !this.transaction?.transacaoPaiId)
  }

  /**
   * Executa a operação de salvamento com controle de loading state.
   * Neste modal de gerenciamento, o salvamento corresponde à regeneração
   * das ocorrências da transação recorrente.
   * Guard contra clique duplo: se já está salvando, ignora chamadas subsequentes.
   * @returns {Promise<void>}
   */
  async _handleSave() {
    if (this._saving) return

    this._saving = true

    try {
      await this._regenerarRecorrencia()
    } finally {
      this._saving = false
    }
  }

  /**
   * Regenera todas as ocorrências futuras da transação recorrente
   * Faz POST ao endpoint de regeneração do backend, que desativa filhas antigas
   * e gera novas com base nos dados atuais da transação pai.
   * Após sucesso, invalida caches e dispara evento para atualizar componentes.
   * @returns {Promise<void>}
   */
  async _regenerarRecorrencia() {
    // Confirmação obrigatória — operação destrutiva (desativa filhas existentes)
    if (!confirm('Isso irá recriar todas as ocorrências futuras com base nos dados atuais. Deseja continuar?')) {
      return
    }

    // Ativa estado de loading no botão
    this.regenerating = true
    this.requestUpdate()

    try {
      // Chamada ao endpoint de regeneração
      const response = await apiService.post(
        `transacoes/${this.transaction.id}/regenerar-recorrencia`
      )

      // Atualiza lista de ocorrências com as novas filhas retornadas pelo backend
      this.ocorrencias = response.data?.transacoesFilhas || []
      this._regeneracaoRealizada = true

      // Invalida todos os caches de transações (filhas podem estar em meses diferentes)
      await transacaoCacheService.recarregar()

      // Dispara evento para que outros componentes atualizem seus dados
      window.dispatchEvent(new CustomEvent('transacoes-cache-updated'))
    } catch (error) {
      alert(error.message || 'Erro ao regenerar recorrência')
    } finally {
      // Restaura estado do botão independente de sucesso ou erro
      this.regenerating = false
      this.requestUpdate()
    }
  }

  /**
   * Fecha o modal ao clicar no backdrop (área escurecida)
   * Delega para this.close() que já gerencia o estado do modal
   */
  _handleBackdropClick() {
    this.close()
  }

  /**
   * Impede propagação do clique no card para o backdrop
   * Evita que cliques dentro do modal disparem o fechamento
   * @param {Event} e - Evento de clique
   */
  _stopPropagation(e) {
    e.stopPropagation()
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Salvar/Regenerar
   * Replica o padrão do floating-button com transição suave de cor
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onSaveHover(e) {
    e.target.style.backgroundColor = 'var(--hover-primary, #7B3EF0)'
  }

  /**
   * Restaura cor original do botão Salvar/Regenerar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onSaveLeave(e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Cancelar
   * Usa background secundário para destacar o botão neutro
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onCancelHover(e) {
    e.target.style.backgroundColor = 'var(--glass-bg, rgba(117, 133, 255, 0.22))'
  }

  /**
   * Restaura cor original do botão Cancelar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onCancelLeave(e) {
    e.target.style.backgroundColor = ''
  }

  render() {
    if (!this.transaction) return html``

    return html`
      <div 
        class="modal ${this.open ? 'is-active' : ''}" 
        @click="${this._handleBackdropClick}"
        style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 30000;
          display: ${this.open ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
        "
      >
        <div 
          class="card"
          @click="${this._stopPropagation}"
          style="
            width: 90%;
            max-width: 700px;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border-radius: var(--radius-card, 20px);
            box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
          "
        >
          <!-- Zona 1: Header fixo no topo com sombra sobre o conteúdo -->
          <header class="card-header" style="flex-shrink: 0; position: relative; z-index: 2; box-shadow: var(--shadow-header-modal, 0 4px 12px rgba(0,0,0,0.3));">
            ${this.renderBotaoVoltarIOS()}
            <p class="card-header-title" data-cy="modal-recorrencias-title">
              Gerenciar Recorrência - ${this.transaction.description}
            </p>
            ${this.renderBotaoFechar('modal-recorrencias-close')}
          </header>

          <!-- Zona 2: Conteúdo com scroll independente -->
          <div class="card-content custom-scrollbar" style="
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            min-height: 0;
          ">
            <p class="subtitle is-6 has-text-grey mb-4">Visualize e gerencie as ocorrências desta transação.</p>

            <!-- Informações Gerais -->
            <div class="card mb-5">
              <div class="card-content">
                <div class="columns is-multiline">
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">Tipo</p>
                    <p class="has-text-weight-medium">Recorrente</p>
                  </div>
                  ${this._getRecurringEndInfo()}
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">Frequência</p>
                    <p class="has-text-weight-medium">Mensal</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Lista de Ocorrências -->
            <div class="mb-4">
              <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
                <h3 class="title is-6">Ocorrências</h3>
                <div class="is-flex" style="gap: 0.5rem;">
                  ${this._isTransacaoPaiRecorrente() ? html`
                    <button 
                      class="button is-warning is-small ${this.regenerating ? 'is-loading' : ''}"
                      data-cy="btn-regenerar-recorrencia"
                      ?disabled="${this.regenerating}"
                      @click="${this._regenerarRecorrencia}"
                    >
                      <span class="icon is-small">
                        <i class="fas fa-sync-alt"></i>
                      </span>
                      <span>Regenerar Ocorrências</span>
                    </button>
                  ` : ''}
                  <button 
                    class="button is-danger is-small"
                    data-cy="btn-delete-all-occurrences"
                    @click="${this._deleteAllOccurrences}"
                  >
                    <span class="icon is-small">
                      <i class="fas fa-trash"></i>
                    </span>
                    <span>Excluir Toda a Série</span>
                  </button>
                </div>
              </div>

              <div class="space-y-2">
                ${this.occurrences.map(occurrence => html`
                  <div class="card">
                    <div class="card-content p-4">
                      <div class="is-flex is-justify-content-space-between is-align-items-center">
                        <div class="is-flex-grow-1">
                          <div class="is-flex is-align-items-center mb-1" style="gap: 0.5rem;">
                            ${occurrence.number ? html`<span class="tag is-info is-light">${occurrence.number}</span>` : ''}
                            <span>${occurrence.date}</span>
                            ${occurrence.isCurrent ? html`
                              <span class="tag is-success is-light">Atual</span>
                            ` : ''}
                          </div>
                          <p class="has-text-grey">${this._formatCurrency(occurrence.value)}</p>
                        </div>
                        <div class="is-flex" style="gap: 0.5rem;">
                          <button 
                            class="button is-small is-ghost"
                            data-cy="btn-edit-occurrence"
                            @click="${() => this._editOccurrence(occurrence.number || 1)}"
                          >
                            <span class="icon is-small">
                              <i class="fas fa-pen"></i>
                            </span>
                          </button>
                          <button 
                            class="button is-small is-ghost"
                            data-cy="btn-delete-occurrence"
                            @click="${() => this._deleteOccurrence(occurrence.number || 1)}"
                          >
                            <span class="icon is-small">
                              <i class="fas fa-trash"></i>
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                `)}
              </div>
            </div>

            <!-- Ocorrências Regeneradas -->
            ${this.ocorrencias.length > 0 ? html`
              <div class="mb-4" data-cy="lista-ocorrencias-regeneradas">
                <h3 class="title is-6">Ocorrências Regeneradas</h3>
                <div class="space-y-2">
                  ${this.ocorrencias.map(ocorrencia => html`
                    <div class="card" data-cy="ocorrencia-regenerada">
                      <div class="card-content p-4">
                        <div class="is-flex is-justify-content-space-between is-align-items-center">
                          <div>
                            <span>${ocorrencia.data || ''}</span>
                          </div>
                          <div>
                            <span class="has-text-weight-medium">${this._formatCurrency(ocorrencia.valor || 0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  `)}
                </div>
              </div>
            ` : ''}
            ${this.ocorrencias.length === 0 && this._regeneracaoRealizada ? html`
              <div class="mb-4" data-cy="msg-nenhuma-ocorrencia">
                <p class="has-text-grey has-text-centered">Nenhuma ocorrência futura</p>
              </div>
            ` : ''}
          </div>

          <!-- Zona 3: Footer fixo com botões de ação e sombra sobre o conteúdo -->
          <footer class="card-footer"
            data-cy="footer-modal-recorrencias"
            aria-label="Ações do modal de recorrências"
            style="
              flex-shrink: 0;
              padding: 1rem 1.5rem;
              background-color: var(--bg-surface, #151C30);
              border-top: 1px solid var(--border, #26304A);
              position: relative;
              z-index: 2;
              box-shadow: var(--shadow-footer-modal, 0 -4px 12px rgba(0,0,0,0.3));
            "
          >
            <div class="field is-grouped is-justify-content-flex-end" style="width: 100%">
              <div class="control">
                <button 
                  class="button" 
                  type="button" 
                  data-cy="btn-close-recorrencias"
                  ?disabled="${this._saving}"
                  @click="${this.close}"
                  @mouseenter="${this._onCancelHover}"
                  @mouseleave="${this._onCancelLeave}"
                >
                  Fechar
                </button>
              </div>
              <div class="control">
                <button 
                  class="button is-primary ${this._saving ? 'is-loading' : ''}" 
                  type="button" 
                  data-cy="btn-salvar-recorrencias"
                  ?disabled="${this._saving}"
                  @click="${this._handleSave}"
                  @mouseenter="${this._onSaveHover}"
                  @mouseleave="${this._onSaveLeave}"
                >
                  Regenerar
                </button>
              </div>
            </div>
          </footer>
        </div>
      </div>
    `
  }
}

customElements.define('modal-recorrencias', ModalRecorrencias)
